'use client';

import { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';

import { useToggle } from '@/hooks/use-toggle';
import { useWindowDimensions } from '@/hooks/use-window-dimensions';
import { useTranslation } from '@/i18n/client';
import { FilterItem, FilterPriceValues } from '@/types/filters';
import { WithLang } from '@/types/general';

interface SelectFilterProps {
    onClick: (filterItem: FilterItem) => void;
    options: FilterItem[];
    defaultValue: string;
    selectType: 'input' | 'select';
}

export default function SelectFilter(props: WithLang<SelectFilterProps>) {
    const { toggleValue, handleToggle, setToggleValue } = useToggle();
    const {
        dimensions: { height: windowHeight },
    } = useWindowDimensions();

    const { t } = useTranslation(props.lang, 'filters');

    const [optionsHeight, setOptionsHeight] = useState(0);
    const [options, setOptions] = useState<FilterItem[]>(props.options);
    const [inputValue, setInputValue] = useState('');

    const isAllOptionsSelected = options.every((option) => option.isSelected);

    const filterOptions = useCallback(() => {
        return props.options.filter(
            (item: FilterItem) => !item.isSelected && item.display.toLowerCase().includes(inputValue.toLowerCase()),
        );
    }, [inputValue, props.options]);

    const handleFilterOptions = useCallback(() => {
        setOptions(filterOptions());
    }, [filterOptions]);

    useEffect(() => {
        handleFilterOptions();
        inputValue.length > 0 ? setToggleValue(true) : setToggleValue(false);
    }, [handleFilterOptions, inputValue, props.options, setToggleValue]);

    useEffect(() => {
        const calcOptionsHeight = () => {
            const selectTopOffset = selectRef.current?.offsetTop;
            const bottomOffset = 100;
            if (selectTopOffset) {
                const resultHeight = windowHeight - (selectTopOffset + bottomOffset) + window.scrollY;
                setOptionsHeight(resultHeight);
            }
        };

        calcOptionsHeight();

        window.addEventListener('scroll', () => calcOptionsHeight());
        return () => removeEventListener('scroll', calcOptionsHeight);
    }, [windowHeight]);

    const handleSelect = (e: SyntheticEvent, option: FilterItem) => {
        e.stopPropagation();
        props.onClick(option);
    };

    useEffect(() => {
        if (isAllOptionsSelected) {
            setToggleValue(false);
        }
    }, [props.options, setToggleValue, isAllOptionsSelected]);

    const selectRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setToggleValue(false);
            }
        };

        document.addEventListener('click', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [setToggleValue, isAllOptionsSelected]);

    const openSelect = () => {
        setOptions(filterOptions());
        if (!isAllOptionsSelected) {
            handleToggle();
        }
    };

    return (
        <div ref={selectRef} className="select text-regular relative flex">
            <div
                onClick={openSelect}
                className={`flex w-full justify-between border border-black px-4 ${
                    isAllOptionsSelected ? 'cursor-default opacity-50' : 'cursor-pointer'
                } ${toggleValue ? 'border-opacity-40' : 'border-opacity-10'}`}
            >
                {props.selectType === 'select' ? (
                    <>
                        <span className="inherit mr-2 py-3">{props.defaultValue}</span>
                        <span className="icon w-3 bg-phiCaretDown" />
                    </>
                ) : (
                    <>
                        <input
                            className="inherit mr-2 w-full py-3 outline-none placeholder:text-black"
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={props.defaultValue}
                        />
                        <span className="icon w-3 bg-phiCaretDown" />
                    </>
                )}
            </div>
            <ul
                style={{ maxHeight: optionsHeight }}
                className={`select-options absolute top-[125%] z-[2] flex max-h-[145px] min-h-[48px] w-full flex-col items-start overflow-y-auto border border-black border-opacity-40 bg-white py-4 ${
                    !toggleValue || isAllOptionsSelected ? 'hidden' : ''
                }`}
            >
                {options.map((option: FilterItem) => (
                    <button
                        key={option.value}
                        className={`w-full cursor-pointer px-5 py-2 text-left transition-colors duration-100 hover:bg-accent hover:bg-opacity-20 ${
                            option.isSelected ? 'hidden' : ''
                        }`}
                        onClick={(e) => handleSelect(e, option)}
                    >
                        {option.key === 'price'
                            ? t(`priceOptions.${option.display as FilterPriceValues}`)
                            : option.display}
                    </button>
                ))}
            </ul>
        </div>
    );
}
